/**
 * React Query hooks for Companies module
 * React Query hooks สำหรับโมดูลบริษัท
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as api from './companies.api'
import type { CompanyFilters, UpdateCompanyInput } from './types'

// Query keys
const COMPANIES_KEY = 'companies'
const COMPANY_KEY = 'company'
const PROJECTS_KEY = 'projects'
const DOCUMENTS_KEY = 'documents'
const HISTORY_KEY = 'history'
const ACTIVITY_KEY = 'activity'

/**
 * Hook for fetching companies list
 * Hook สำหรับดึงรายการบริษัท
 */
export function useCompanies(filters: CompanyFilters) {
  console.log('[useCompanies] Called with filters:', filters)
  return useQuery({
    queryKey: [COMPANIES_KEY, filters],
    queryFn: async () => {
      console.log('[useCompanies] Executing queryFn')
      const params: { search?: string; status?: string; page?: number; pageSize?: number } = {
        page: filters.page,
        pageSize: filters.pageSize,
      }
      if (filters.search) params.search = filters.search
      if (filters.status !== 'all') params.status = filters.status
      console.log('[useCompanies] Calling API with params:', params)
      const result = await api.getCompanies(params)
      console.log('[useCompanies] API result:', result)
      return result
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook for fetching single company
 * Hook สำหรับดึงข้อมูลบริษัทเดียว
 */
export function useCompany(id: string | undefined) {
  return useQuery({
    queryKey: [COMPANY_KEY, id],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => api.getCompany(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for creating company
 * Hook สำหรับสร้างบริษัท
 */
export function useCreateCompany() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.createCompany,
    onSuccess: () => {
      // Invalidate companies list
      void queryClient.invalidateQueries({ queryKey: [COMPANIES_KEY] })
    },
  })
}

/**
 * Hook for updating company
 * Hook สำหรับอัปเดตบริษัท
 */
export function useUpdateCompany() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCompanyInput }) =>
      api.updateCompany(id, input),
    onSuccess: (_, { id }) => {
      // Invalidate specific company
      void queryClient.invalidateQueries({ queryKey: [COMPANY_KEY, id] })
      // Invalidate companies list
      void queryClient.invalidateQueries({ queryKey: [COMPANIES_KEY] })
    },
  })
}

/**
 * Hook for deleting company
 * Hook สำหรับลบบริษัท
 */
export function useDeleteCompany() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.deleteCompany,
    onSuccess: () => {
      // Invalidate companies list
      void queryClient.invalidateQueries({ queryKey: [COMPANIES_KEY] })
    },
  })
}

/**
 * Hook for fetching company projects
 * Hook สำหรับดึงแฟ้มงานของบริษัท
 */
export function useCompanyProjects(companyId: string | undefined) {
  return useQuery({
    queryKey: [COMPANY_KEY, companyId, PROJECTS_KEY],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => api.getCompanyProjects(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook for fetching company documents
 * Hook สำหรับดึงเอกสารของบริษัท
 */
export function useCompanyDocuments(companyId: string | undefined) {
  return useQuery({
    queryKey: [COMPANY_KEY, companyId, DOCUMENTS_KEY],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => api.getCompanyDocuments(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook for uploading document
 * Hook สำหรับอัปโหลดเอกสาร
 */
export function useUploadDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      companyId,
      file,
      source,
      relatedYear,
    }: {
      companyId: string
      file: File
      source: 'initial' | 'change'
      relatedYear?: string
    }) => api.uploadCompanyDocument(companyId, file, source, relatedYear),
    onSuccess: (_, { companyId }) => {
      void queryClient.invalidateQueries({
        queryKey: [COMPANY_KEY, companyId, DOCUMENTS_KEY],
      })
    },
  })
}

/**
 * Hook for deleting document
 * Hook สำหรับลบเอกสาร
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ companyId, documentId }: { companyId: string; documentId: string }) =>
      api.deleteCompanyDocument(companyId, documentId),
    onSuccess: (_, { companyId }) => {
      void queryClient.invalidateQueries({
        queryKey: [COMPANY_KEY, companyId, DOCUMENTS_KEY],
      })
    },
  })
}

/**
 * Hook for fetching change history
 * Hook สำหรับดึงประวัติการเปลี่ยนแปลง
 */
export function useChangeHistory(companyId: string | undefined) {
  return useQuery({
    queryKey: [COMPANY_KEY, companyId, HISTORY_KEY],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => api.getChangeHistory(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook for fetching activity log
 * Hook สำหรับดึงบันทึกกิจกรรม
 */
export function useActivityLog(companyId: string | undefined) {
  return useQuery({
    queryKey: [COMPANY_KEY, companyId, ACTIVITY_KEY],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => api.getActivityLog(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook for prefetching company (for router loaders)
 * Hook สำหรับ prefetch บริษัท (สำหรับ router loaders)
 */
export function usePrefetchCompany() {
  const queryClient = useQueryClient()

  return (id: string) => {
    void queryClient.prefetchQuery({
      queryKey: [COMPANY_KEY, id],
      queryFn: () => api.getCompany(id),
      staleTime: 5 * 60 * 1000,
    })
  }
}
